/**
 * Componente principal de la aplicaciÃ³n
 * 
 * Configura el enrutamiento principal y maneja la autenticaciÃ³n global.
 * 
 * @module App
 * @requires react, react-router-dom, ./contexts/AuthContext, ./components/*, ./pages/*
 */

import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Contextos
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Componentes de Layout
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import RequirePermission from './components/common/RequirePermission';
import MercadoPagoReturnHandler from './components/billing/MercadoPagoReturnHandler';

// PÃ¡ginas pÃºblicas
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import VerificarEmailEmpresa from './pages/auth/VerificarEmailEmpresa';
import NotFound from './pages/NotFound';

// PÃ¡ginas protegidas
import Dashboard from './pages/Dashboard';
// MÃ³dulo de clientes
import Clientes from './pages/clientes/Clientes';
import ClienteForm from './pages/clientes/ClienteForm';
import CuentaCorrienteCliente from './pages/clientes/CuentaCorrienteCliente';
import CuentaCorriente from './pages/cuenta-corriente/CuentaCorriente';
import CentroOperaciones from './pages/centro-operaciones/CentroOperaciones';
import CentroComercialHub from './pages/centro-comercial/CentroComercialHub';
import FlyerStudio from './pages/centro-comercial/flyer/FlyerStudio';
import ImportarClientes from './pages/clientes/ImportarClientes';
// MÃ³dulo de Productos
import Productos from './pages/productos/Productos';
import EquivalenciasProveedor from './pages/productos/EquivalenciasProveedor';
import ListasProveedor from './pages/productos/ListasProveedor';
import ComparadorPrecios from './pages/productos/ComparadorPrecios';
import ProductoForm from './pages/productos/ProductoForm';
import ProductoDetalle from './pages/productos/ProductoDetalle';
import ActivacionMasivaPendientes from './pages/productos/ActivacionMasivaPendientes';
//control de stock
import ControlStock from './pages/stock/ControlStock';
import HistorialControl from './pages/stock/HistorialControl';
import DetalleControl from './pages/stock/DetalleControl';
import SolicitudesAjuste from './pages/stock/SolicitudesAjuste';
// MÃ³dulo de Compras
import Compras from './pages/compras/Compras';
import CompraForm from './pages/compras/CompraForm';
import CompraDetalle from './pages/compras/CompraDetalle';

// MÃ³dulo de Ventas
import Ventas from './pages/ventas/Ventas';
import VentaForm from './pages/ventas/VentaForm';
import DetalleVenta from './pages/ventas/DetalleVenta';
import PuntoVenta from './pages/ventas/PuntoVenta';

// MÃ³dulo de Stock
import Stock from './pages/stock/Stock';
import AjusteStock from './pages/stock/AjusteStock';
import InicializarStock from './pages/stock/InicializarStock';
import TransferenciasStock from './pages/stock/TransferenciasStock';
import NuevaTransferencia from './pages/stock/NuevaTransferencia';
import TransferenciaDetalle from './pages/stock/TransferenciaDetalle';
import MovimientosProducto from './pages/stock/MovimientosProducto';

// MÃ³dulo de Reportes
import Reportes from './pages/reportes/Reportes';
import ReporteVentas from './pages/reportes/ReporteVentas';
import ReporteCompras from './pages/reportes/ReporteCompras';
import ReporteGanancias from './pages/reportes/ReporteGanancias';
import ReporteEstadisticasProducto from './pages/reportes/ReporteEstadisticasProducto';

// MÃ³dulo de Promociones
import Promociones from './pages/promociones/Promociones';
import PromocionForm from './pages/promociones/PromocionForm';

// MÃ³dulo de CategorÃ­as
import Categorias from './pages/categorias/Categorias';
import CategoriaForm from './pages/categorias/CategoriaForm';
import CategoriaDetalle from './pages/categorias/CategoriaDetalle';

// MÃ³dulo de Usuarios
import Usuarios from './pages/usuarios/Usuarios';
import UsuarioForm from './pages/usuarios/UsuarioForm';
import CambioPassword from './pages/usuarios/CambioPassword';
import Perfil from './pages/usuarios/Perfil';
import PermisosManagement from './pages/usuarios/PermisosManagement';
//materias primas
import MateriasPrimas from './pages/materiasPrimas/MateriasPrimas';
import MateriaPrimaForm from './pages/materiasPrimas/MateriaPrimaForm';
//recetas
import Recetas from './pages/recetas/Recetas';
import RecetaForm from './pages/recetas/RecetaForm';
import RecetaDetalle from './pages/recetas/RecetaDetalle';
//produccion
import Produccion from './pages/produccion/Produccion';
import ProduccionForm from './pages/produccion/ProduccionForm';
import ProduccionDetalle from './pages/produccion/ProduccionDetalle';
//listas de precios
import GestionPrecios from './pages/productos/GestionPrecios';
// Combos C1A
import Combos from './pages/combos/Combos';
import ComboForm from './pages/combos/ComboForm';
import Caja from './pages/caja/Caja';
import Gastos from './pages/finanzas/Gastos';
import CuentasPorPagar from './pages/finanzas/CuentasPorPagar';
import Devoluciones from './pages/devoluciones/Devoluciones';
import DevolucionForm from './pages/devoluciones/DevolucionForm';
import Transferencias from './pages/transferencias/Transferencias';
import TransferenciaForm from './pages/transferencias/TransferenciaForm';
import VentasEliminadas from './pages/ventas/VentasEliminadas';
import Auditoria from './pages/auditoria/Auditoria';
// âœ… NUEVO: ConfiguraciÃ³n empresarial
import ConfiguracionEmpresa from './pages/configuracion/configuracionempresa';
// MÃ³dulo de Proveedores
import Proveedores from './pages/proveedores/Proveedores';
import ProveedorForm from './pages/proveedores/ProveedorForm';
import ProveedorCuentaCorriente from './pages/proveedores/ProveedorCuentaCorriente';
import Sucursales from './pages/sucursales/Sucursales';
import MobilePuntoVenta from './components/mobile/MobilePuntoVenta';
import AdminPanel from './pages/admin/AdminPanel';
import SuperAdminRoute from './components/common/SuperAdminRoute';
import AppBootstrapSplash, { dismissAppBootstrapSplash } from './components/common/AppBootstrapSplash';
import AppVersionGuard from './components/common/AppVersionGuard';
import { useViewportHeight } from './hooks/useViewportHeight';
import { ACCESS_MODES, getStoredAccessMode, isAdminLikeRole, setStoredAccessMode } from './utils/runtimeAccessMode';

const NATIVE_PUBLIC_PATH_PREFIXES = ['/login', '/signup', '/verificar-email'];

const isNativeCapacitorRuntime = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window?.NexoAndroid) return true;
    if (
      !!window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    ) {
      return true;
    }
    const ua = String(window?.navigator?.userAgent || '').toLowerCase();
    const isAndroidWebView = ua.includes('android') && (ua.includes('; wv') || ua.includes('version/'));
    return isAndroidWebView;
  } catch {
    return false;
  }
};

const TABLET_PORTRAIT_SCROLL_MQ = '(orientation: portrait) and (max-width: 1024px)';

const isCajeroPath = (pathname) => {
  const p = String(pathname || '/').toLowerCase();
  return p === '/cajero' || p.startsWith('/cajero/');
};

/** Habilita scroll documental en tablet vertical fuera de /cajero (PWA/admin). */
const TabletPortraitScrollManager = () => {
  const { pathname } = useLocation();
  const isCajero = isCajeroPath(pathname);

  useEffect(() => {
    const html = document.documentElement;
    const mq = window.matchMedia(TABLET_PORTRAIT_SCROLL_MQ);
    const sync = () => {
      if (!isCajero && mq.matches) html.classList.add('nexo-tablet-doc-scroll');
      else html.classList.remove('nexo-tablet-doc-scroll');
    };
    sync();
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
      html.classList.remove('nexo-tablet-doc-scroll');
    };
  }, [isCajero]);

  return null;
};

const RouteOutlet = ({ children }) => {
  const { pathname } = useLocation();
  const isCajero = isCajeroPath(pathname);
  const cls = isCajero
    ? 'nexo-route-outlet flex min-h-0 flex-1 flex-col overflow-hidden'
    : 'nexo-route-outlet nexo-route-outlet--scroll flex min-h-0 flex-1 flex-col overflow-hidden';
  return <div className={cls}>{children}</div>;
};

const CajeroApp = () => (
  <div className="cajero-app-shell flex h-full min-h-0 w-full flex-col overflow-hidden bg-e0e3-bg px-1.5 pt-1 pb-1 sm:px-2">
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <MobilePuntoVenta />
    </div>
  </div>
);

const RootRouteGateway = () => {
  const { currentUser } = useAuth() || {};
  const nativeRuntime = isNativeCapacitorRuntime();
  if (typeof window !== 'undefined' && sessionStorage.getItem('postVerifyGoConfig') === '1') {
    return (
      <Navigate
        to="/configuracion/empresa"
        replace
        state={{ openWizardModal: true, onboardingSource: 'standard' }}
      />
    );
  }
  if (nativeRuntime) {
    const mode = getStoredAccessMode(ACCESS_MODES.CAJERO);
    if (mode === ACCESS_MODES.ADMIN && isAdminLikeRole(currentUser)) {
      return <Dashboard />;
    }
    return <Navigate to="/cajero" replace />;
  }
  return <Dashboard />;
};


/** UX-FLOW: /proveedores/:id huerfano -> cuenta corriente canonica */
const ProveedorIdRedirect = () => {
  const { id } = useParams();
  const safe = Boolean(id && /^[A-Za-z0-9_-]+$/.test(id));
  if (!safe) {
    return <Navigate to="/proveedores" replace />;
  }
  return <Navigate to={`/proveedores/${id}/cuenta-corriente`} replace />;
};

/** UX-FLOW: rutas legacy facturas/pagos -> proveedores */
const LegacyCxPRedirect = () => {
  useEffect(() => {
    toast.info(
      'Las facturas y pagos de proveedores se administran desde su cuenta corriente.',
      { toastId: 'legacy-cxp-redirect', autoClose: 5000 }
    );
  }, []);
  return <Navigate to="/proveedores" replace />;
};

const AdminRouteGateway = () => <AdminPanel />;

const NativeRouteEnforcer = () => {
  const nativeRuntime = isNativeCapacitorRuntime();
  const auth = useAuth() || {};
  const { isAuthenticated, currentUser } = auth;
  const location = useLocation();
  const navigate = useNavigate();
  const cleanedLegacyCacheRef = useRef(false);

  useEffect(() => {
    if (!nativeRuntime || cleanedLegacyCacheRef.current) return;
    cleanedLegacyCacheRef.current = true;

    (async () => {
      try {
        if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((reg) => reg.unregister()));
        }
      } catch {
        // ignore
      }
      try {
        if (typeof window !== 'undefined' && window.caches?.keys) {
          const keys = await window.caches.keys();
          await Promise.all(keys.map((key) => window.caches.delete(key)));
        }
      } catch {
        // ignore
      }
    })();
  }, [nativeRuntime]);

  useEffect(() => {
    if (!nativeRuntime) return;
    const path = String(location.pathname || '/').toLowerCase();
    const storedMode = getStoredAccessMode(ACCESS_MODES.CAJERO);
    const adminRole = isAdminLikeRole(currentUser);

    if (!isAuthenticated) return;

    const isPublicPath = NATIVE_PUBLIC_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
    if (isPublicPath) return;

    if (storedMode === ACCESS_MODES.ADMIN && adminRole) {
      // Admin embebido completo: no restringimos mÃ³dulos internos.
      return;
    }

    if (storedMode === ACCESS_MODES.ADMIN && !adminRole) {
      setStoredAccessMode(ACCESS_MODES.CAJERO);
    }
    if (!(path === '/cajero' || path.startsWith('/cajero/'))) {
      navigate('/cajero', { replace: true });
    }
  }, [location.pathname, nativeRuntime, navigate, isAuthenticated, currentUser]);

  return null;
};

const AppContent = () => {
  useViewportHeight();
  const auth = useAuth() || {};
  const { loading } = auth;

  useEffect(() => {
    if (!loading) {
      dismissAppBootstrapSplash();
    }
  }, [loading]);

  if (loading) {
    return (
      <AppBootstrapSplash
        productName="0E3 POS"
        message="Preparando tu espacio de trabajo..."
        fullViewport
      />
    );
  }

  return (
    <>
    <div className="nexo-viewport-root">
    <Router>
      <AppVersionGuard />
      <MercadoPagoReturnHandler />
      <NativeRouteEnforcer />
      <TabletPortraitScrollManager />
      <RouteOutlet>
      <Routes>
        {/* Rutas pÃºblicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {/* Onboarding sin Layout: autenticado pero aÃºn sin orgId */}
        <Route path="/verificar-email" element={<ProtectedRoute><VerificarEmailEmpresa /></ProtectedRoute>} />
        <Route path="/cajero" element={<ProtectedRoute><CajeroApp /></ProtectedRoute>} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/configuracion/empresa" element={<ConfiguracionEmpresa />} />
            <Route path="/" element={<RootRouteGateway />} />
            <Route path="/dashboard" element={<RootRouteGateway />} />
            <Route path="/centro-operaciones" element={<CentroOperaciones />} />
            <Route path="/centro-comercial" element={<CentroComercialHub />} />
            <Route path="/centro-comercial/flyer" element={<FlyerStudio />} />
            <Route path="/centro-comercial/:modulo" element={<CentroComercialHub />} />
            <Route path="/admin" element={<SuperAdminRoute><AdminRouteGateway /></SuperAdminRoute>} />
            
            {/* MÃ³dulo de Productos */}
            <Route path="/productos" element={<Productos />} />
            <Route path="/productos/activacion-masiva" element={<ActivacionMasivaPendientes />} />
            <Route path="/productos/nuevo" element={<ProductoForm />} />
            <Route path="/productos/editar/:id" element={<ProductoForm />} />
            <Route path="/productos/:id" element={<ProductoDetalle />} />
			<Route path="/productos/precios" element={<RequirePermission module="listas_precios" action="ver"><GestionPrecios /></RequirePermission>} />
            <Route path="/productos/equivalencias" element={<RequirePermission module="productos" action="ver"><EquivalenciasProveedor /></RequirePermission>} />
            <Route path="/productos/listas-proveedor" element={<RequirePermission module="productos" action="ver"><ListasProveedor /></RequirePermission>} />
            <Route path="/productos/comparador-precios" element={<RequirePermission module="productos" action="ver"><ComparadorPrecios /></RequirePermission>} />
            <Route path="/combos" element={<RequirePermission module="combos" action="ver"><Combos /></RequirePermission>} />
            <Route path="/combos/nuevo" element={<RequirePermission module="combos" action="crear"><ComboForm /></RequirePermission>} />
            <Route path="/combos/editar/:id" element={<RequirePermission module="combos" action="editar"><ComboForm /></RequirePermission>} />

           {/* MÃ³dulo de Clientes - ORDEN IMPORTANTE */}
			<Route path="/clientes/nuevo" element={<ClienteForm />} />
			<Route path="/clientes/:id/cuenta-corriente" element={<CuentaCorrienteCliente />} />
			<Route path="/clientes/editar/:id" element={<ClienteForm />} />
			<Route path="/clientes/importar" element={<ImportarClientes />} />
			<Route path="/clientes" element={<Clientes />} />
			<Route path="/cuenta-corriente" element={<CuentaCorriente />} />
            {/* MÃ³dulo de Compras */}
            <Route path="/compras" element={<Compras />} />
            <Route path="/compras/nueva" element={<RequirePermission module="compras" action="crear"><CompraForm /></RequirePermission>} />
            <Route path="/compras/:id" element={<CompraDetalle />} />
            
            {/* MÃ³dulo de Ventas */}
            <Route path="/ventas" element={<Ventas />} />
            <Route path="/ventas/nueva" element={<VentaForm />} />
            <Route path="/ventas/:id" element={<DetalleVenta />} />
            <Route path="/punto-venta" element={<PuntoVenta />} />
            <Route path="/ventas-eliminadas" element={<VentasEliminadas />} />
            
            {/* MÃ³dulo de Stock */}
            <Route path="/stock" element={<Stock />} />
            <Route path="/stock/ajustes" element={<Stock />} />
            <Route path="/stock/inicializar" element={<InicializarStock />} />
            <Route path="/stock/ajuste/:id" element={<AjusteStock />} />
            <Route path="/stock/transferencias" element={<TransferenciasStock />} />
            <Route path="/stock/transferencias/nueva" element={<NuevaTransferencia />} />
            
		    <Route path="/stock/transferencias/:id" element={<TransferenciaDetalle />} />
			{/* Historial de movimientos de producto */}
            <Route path="/stock/movimientos/:productoId" element={<MovimientosProducto />} />

            {/* MÃ³dulo de Reportes */}
            <Route path="/reportes" element={<Reportes />} />
            <Route path="/reportes/ventas" element={<ReporteVentas />} />
            <Route path="/reportes/compras" element={<ReporteCompras />} />
            <Route path="/reportes/ganancias" element={<ReporteGanancias />} />
            <Route path="/reportes/productos" element={<ReporteEstadisticasProducto />} />
            <Route path="/reportes/productos-vendidos" element={<Navigate to="/reportes/productos" replace />} />
            <Route path="/reportes/stock" element={<Navigate to="/stock" replace />} />
            <Route path="/reportes/personalizado" element={<Navigate to="/reportes/ganancias" replace />} />
            <Route path="/reportes/cuentas-por-cobrar" element={<Navigate to="/ventas" replace />} />
            
            {/* MÃ³dulo de Promociones */}
            <Route path="/promociones" element={<Promociones />} />
            <Route path="/promociones/nueva" element={<PromocionForm />} />
            <Route path="/promociones/editar/:id" element={<PromocionForm />} />
            
            {/* MÃ³dulo de CategorÃ­as */}
            <Route path="/categorias" element={<Categorias />} />
            <Route path="/categorias/nueva" element={<CategoriaForm />} />
            <Route path="/categorias/editar/:id" element={<CategoriaForm />} />
            <Route path="/categorias/:id" element={<CategoriaDetalle />} />
            
            {/* MÃ³dulo de Usuarios */}
            <Route path="/usuarios" element={<Usuarios />} />
            <Route path="/usuarios/nuevo" element={<UsuarioForm />} />
            <Route path="/usuarios/editar/:id" element={<UsuarioForm />} />
            <Route path="/usuarios/:id/password" element={<CambioPassword />} />
            <Route path="/perfil" element={<Perfil />} />
			{/* MÃ³dulo de Materias Primas */}
			<Route path="/materias-primas" element={<MateriasPrimas />} />
			<Route path="/materias-primas/nueva" element={<MateriaPrimaForm />} />
			<Route path="/materias-primas/editar/:id" element={<MateriaPrimaForm />} />
			{/* MÃ³dulo de Recetas */}
			<Route path="/recetas" element={<Recetas />} />
			<Route path="/recetas/nueva" element={<RecetaForm />} />
			<Route path="/recetas/editar/:id" element={<RecetaForm />} />
			<Route path="/recetas/:id" element={<RecetaDetalle />} />
			{/* MÃ³dulo de ProducciÃ³n */}
			<Route path="/produccion" element={<Produccion />} />
			<Route path="/produccion/nueva" element={<ProduccionForm />} />
			<Route path="/produccion/:id" element={<ProduccionDetalle />} />
			{/* MÃ³dulo de Proveedores */}
			<Route path="/proveedores" element={<Proveedores />} />
			<Route path="/proveedores/nuevo" element={<ProveedorForm />} />
            <Route path="/proveedores/editar/:id" element={<ProveedorForm />} />
			<Route path="/proveedores/:id/cuenta-corriente" element={<ProveedorCuentaCorriente />} />
			<Route path="/facturas" element={<LegacyCxPRedirect />} />
			<Route path="/pagos" element={<LegacyCxPRedirect />} />
			<Route path="/pagos/resumen" element={<LegacyCxPRedirect />} />
			<Route path="/proveedores/:id" element={<ProveedorIdRedirect />} />
			<Route path="/sucursales" element={<Sucursales />} />
			{/* Nuevos MÃ³dulos */}
			<Route path="/caja" element={<Caja />} />
			<Route path="/finanzas/gastos" element={<Gastos />} />
			<Route path="/finanzas/cuentas-por-pagar" element={<CuentasPorPagar />} />
			<Route path="/gastos" element={<Gastos />} />
			<Route path="/gastos/nuevo" element={<Gastos />} />
			<Route path="/devoluciones" element={<Devoluciones />} />
			<Route path="/devoluciones/nueva" element={<DevolucionForm />} />
			<Route path="/listas-precios" element={<Navigate to="/productos/precios" replace />} />
			<Route path="/transferencias" element={<Transferencias />} />
			<Route path="/transferencias/nueva" element={<TransferenciaForm />} />
			<Route path="/auditoria" element={<Auditoria />} />
			<Route path="/usuarios/permisos" element={<PermisosManagement />} />
			
			{/* Control de stock */}
			<Route path="/stock/control" element={<ControlStock />} />
			<Route path="/stock/control/historial" element={<HistorialControl />} />
			<Route path="/stock/control/:id" element={<DetalleControl />} />
			<Route path="/stock/solicitudes-ajuste" element={<SolicitudesAjuste />} />
			
			{/* Compatibilidad: ruta legacy de configuraciÃ³n */}
            <Route path="/configuracion" element={<Navigate to="/configuracion/empresa" replace />} />
			{/* Config empresa dentro del Layout (remaps nexo-admin-content) */}
			
        </Route>

        {/* Ruta 404 para pÃ¡ginas no encontradas */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RouteOutlet>
    </Router>
    </div>
      <ToastContainer
        position="top-center"
        theme="colored"
        autoClose={3800}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastClassName="rounded-2xl shadow-xl border-0 font-medium"
        bodyClassName="text-sm px-1"
        limit={4}
      />
    </>
  );
};

/**
 * Componente principal que contiene todas las rutas de la aplicaciÃ³n
 * @returns {JSX.Element} Componente App
 */
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
